import { ChangeDetectionStrategy, Component, computed, inject, input, ProviderToken, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { connectState } from '@examdojo/angular/util';
import { SyllabusQuery } from '@examdojo/category/v2';
import { ButtonComponent } from '@examdojo/core/button';
import { LoaderComponent } from '@examdojo/core/loader';
import { SmallLayoutComponent, WideLayoutComponent } from '@examdojo/layout';
import {
  provideLocalMcqSelection,
  provideSingleAttemptSelfGradingSelection,
  QUESTION_CONTEXT,
  QUESTION_SELECTOR_DATA_SOURCE,
  QuestionAttemptEntityQuery,
} from '@examdojo/question';
import { FooterPanelComponent } from '@examdojo/ui/footer-panel';
import { isNotNullish } from '@examdojo/util/nullish';
import { TranslocoPipe } from '@jsverse/transloco';
import { filter, finalize, switchMap, tap } from 'rxjs';
import { ChallengeActivityService } from '@examdojo/challenge';
import { ForceSelfGradingOnErrorEffect } from '../effects/force-self-grading-on-error.effect';
import { ExamdojoQuestionComponent } from '../../../question-v2';
import { ChallengeReviewDataService } from './challenge-review-data.service';
import { ChallengeReviewDialogService } from './challenge-review-dialog.service';
import { ChallengeReviewQuestionSelectorComponent } from './challenge-review-question-selector.component';
import { ChallengeReviewQuestionSelectorService } from './challenge-review-question-selector.service';

@Component({
  selector: 'dojo-challenge-review-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FooterPanelComponent,
    ButtonComponent,
    TranslocoPipe,
    ChallengeReviewQuestionSelectorComponent,
    ExamdojoQuestionComponent,
    SmallLayoutComponent,
    WideLayoutComponent,
    LoaderComponent,
    NgClass,
  ],
  templateUrl: './challenge-review-dialog.component.html',
  host: { class: 'flex flex-col h-full' },
  providers: [
    ChallengeReviewDataService,
    ChallengeReviewQuestionSelectorService,
    { provide: QUESTION_SELECTOR_DATA_SOURCE, useExisting: ChallengeReviewQuestionSelectorService },
    ForceSelfGradingOnErrorEffect,
    provideLocalMcqSelection(),
    provideSingleAttemptSelfGradingSelection(),
    {
      provide: QUESTION_CONTEXT,
      useFactory: () => {
        const syllabusQuery = inject(SyllabusQuery);
        const activeProgram = toSignal(syllabusQuery.activeProgram$);

        return {
          showHints: true,
          optionsSelectable: false,
          revealSolutionOnWrongAnswer: true,
          hideCalculatorIndicator: computed(() => activeProgram()?.code === 'AP'),
        };
      },
    },
  ],
})
export class ChallengeReviewDialogComponent {
  constructor(
    private readonly challengeReviewDialogService: ChallengeReviewDialogService,
    private readonly challengeReviewDataService: ChallengeReviewDataService,
    private readonly questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly challengeActivityService: ChallengeActivityService,
  ) {
    this.loadReviewDataEffect().pipe(takeUntilDestroyed()).subscribe();
  }

  private readonly instantiatedServices = [ForceSelfGradingOnErrorEffect].map((service: ProviderToken<unknown>) =>
    inject(service),
  );

  readonly challengeActivityId = input.required<string>();
  readonly isLoading = signal(false);

  private readonly challengeActivityId$ = toObservable(this.challengeActivityId);

  readonly state = connectState({
    activeQuestionAttempt: this.questionAttemptQuery.active$,
    isQuestionMultipleChoice: this.questionAttemptQuery.isQuestionMultipleChoice$,
  });

  close() {
    this.challengeActivityService.resetDialogStores();
    this.challengeReviewDialogService.dismiss();
  }

  private loadReviewDataEffect() {
    return this.challengeActivityId$.pipe(
      filter(isNotNullish),
      tap(() => this.isLoading.set(true)),
      switchMap((activityId) =>
        this.challengeReviewDataService.fetchReviewData(activityId).pipe(
          tap(() => this.challengeReviewDataService.setFirstQuestionAsActive(activityId)),
          finalize(() => this.isLoading.set(false)),
        ),
      ),
    );
  }
}
